import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { matchPath } from 'react-router-dom';
import { List, ListSubheader } from '@mui/material';
import type { ListProps } from '@mui/material';
import NavItem from './NavItem';

interface Item {
    path?: string;
    icon?: ReactNode;
    info?: ReactNode;
    children?: Item[];
    title: string;
}

interface NavSectionProps extends ListProps {
    items: Item[];
    pathname: string;
    title: string;
    horizontal: boolean;
    onLeafItemClick?: () => void;
}

const renderNavItems = ({
                            depth = 0,
                            items,
                            pathname,
                            horizontal,
                            onLeafItemClick
                        }: {
    items: Item[];
    pathname: string;
    depth?: number;
    horizontal: boolean;
    onLeafItemClick?: () => void;
}): JSX.Element => (
    <List
        style={
            horizontal
                ? {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2
                }
                : undefined
        }
        disablePadding
    >
        {items.reduce(
            (acc, item) =>
                reduceChildRoutes({
                    acc,
                    item,
                    pathname,
                    depth,
                    horizontal,
                    onLeafItemClick
                }),
            [] as JSX.Element[]
        )}
    </List>
);

const reduceChildRoutes = ({
                               acc,
                               pathname,
                               item,
                               depth,
                               horizontal,
                               onLeafItemClick
                           }: {
    acc: JSX.Element[];
    pathname: string;
    item: Item;
    depth: number;
    horizontal: boolean;
    onLeafItemClick?: () => void;
}): Array<JSX.Element> => {
    const key = `${item.title}-${depth}`;
    const exactMatch = item.path
        ? !!matchPath({ path: item.path, end: true }, pathname)
        : false;

    if (item.children && item.children.length > 0) {
        const partialMatch = item.path
            ? !!matchPath({ path: item.path, end: false }, pathname)
            : false;

        acc.push(
            <NavItem
                active={partialMatch}
                depth={depth}
                icon={item.icon}
                info={item.info}
                key={key}
                path={item.path}
                title={item.title}
            >
                {renderNavItems({
                    depth: depth + 1,
                    items: item.children,
                    pathname,
                    horizontal: false,
                    onLeafItemClick
                })}
            </NavItem>
        );
    } else {
        acc.push(
            <NavItem
                active={exactMatch}
                depth={depth}
                icon={item.icon}
                info={item.info}
                key={key}
                path={item.path}
                title={item.title}
                onClick={onLeafItemClick}
            />
        );
    }

    return acc;
};

const NavSection: FC<NavSectionProps> = (props) => {
    const {
        horizontal,
        items,
        pathname,
        title,
        onLeafItemClick,
        ...other
    } = props;

    return (
        <List
            subheader={
                <ListSubheader
                    disableGutters
                    disableSticky
                    sx={{
                        color: 'text.primary',
                        fontSize: '0.75rem',
                        lineHeight: 2.5,
                        fontWeight: 700,
                        textTransform: 'uppercase'
                    }}
                >
                    {title}
                </ListSubheader>
            }
            {...other}
        >
            {renderNavItems({
                items,
                pathname,
                horizontal,
                onLeafItemClick
            })}
        </List>
    );
};

NavSection.propTypes = {
    items: PropTypes.array,
    pathname: PropTypes.string,
    title: PropTypes.string
};

export default NavSection;
